import { Coords, pageImageData, Point, rotateAngleValue } from './Preview.types';
import { fabric } from 'fabric';
import { convertHexColorToGrayScale } from '../../utils/colors';
import _ from 'lodash';

export const getPointRelativeToImage = (canvasX: number, canvasY: number, image: pageImageData): Point => {
  const x = canvasX - (image.image.left ?? 0) + (image.pixelCrop?.x ?? 0);
  const y = canvasY - (image.image.top ?? 0) + (image.pixelCrop?.y ?? 0);
  return { x, y };
};

export const convertToGrayScale = (obj: fabric.Object | fabric.Group): void => {
  if (obj instanceof fabric.Group) {
    obj.forEachObject((item: fabric.Object | fabric.Group) => convertToGrayScale(item));
  } else {
    if (typeof obj.fill === 'string') {
      obj.fill = convertHexColorToGrayScale(obj.fill);
    }
    if (typeof obj.stroke === 'string') {
      obj.stroke = convertHexColorToGrayScale(obj.stroke);
    }
    if (typeof obj.borderColor === 'string') {
      obj.borderColor = convertHexColorToGrayScale(obj.borderColor);
    }
  }
};

export const makeImageMovable = (image: fabric.Object) => {
  image.set({
    lockMovementX: false,
    lockMovementY: false,
    lockRotation: false,
    lockScalingX: false,
    lockScalingY: false,
  });
};

export const makeImageLocked = (image: fabric.Object) => {
  image.set({
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
  });
};

export const getImageRotateOffsetForIcons = (
  rotateAngle: rotateAngleValue,
  x: number,
  y: number,
): { leftOffset: number; topOffset: number } => {
  switch (rotateAngle) {
    case 90:
      return { leftOffset: -y, topOffset: x };
    case 180:
      return { leftOffset: -x, topOffset: -y };
    case 270:
      return { leftOffset: y, topOffset: -x };
    default:
      return { leftOffset: x, topOffset: y };
  }
};

export const getTargetPageImage = (
  pageImages: pageImageData[],
  x: number,
  y: number,
  margin = 0,
): pageImageData | undefined => {
  const targetImageData: pageImageData | undefined = pageImages.find((imageData: pageImageData) => {
    const imageCoords = imageData.image.getCoords(true, false);
    const xMin = Math.min(...imageCoords.map((point) => point.x)) + margin;
    const xMax = Math.max(...imageCoords.map((point) => point.x)) - margin;
    const yMin = Math.min(...imageCoords.map((point) => point.y)) + margin;
    const yMax = Math.max(...imageCoords.map((point) => point.y)) - margin;
    return x >= xMin && x <= xMax && y >= yMin && y <= yMax;
  });
  return targetImageData;
};

const getParentPageImage = (
  pageImages: pageImageData[],
  pagePositionX: number,
  pagePositionY: number,
  pageNumber: number | null,
): pageImageData | undefined => {
  const pageImage = pageImages.find((item: pageImageData) => {
    if (item.pageNumber !== pageNumber) {
      return false;
    } else if (!item.pixelCrop) {
      return true;
    } else if (
      pagePositionX >= item.pixelCrop.x &&
      pagePositionX <= item.pixelCrop.x + item.pixelCrop.width &&
      pagePositionY >= item.pixelCrop.y &&
      pagePositionY <= item.pixelCrop.y + item.pixelCrop.height
    ) {
      return true;
    } else {
      return false;
    }
  });

  return pageImage;
};

export const translatePagePositionToCanvasCoordinates = (
  pageImages: pageImageData[],
  pagePositionX: number,
  pagePositionY: number,
  pageNumber: number | null,
): Coords | null => {
  const pageImage = getParentPageImage(pageImages, pagePositionX, pagePositionY, pageNumber);
  if (pageImage && pageImage.image.left !== undefined && pageImage.image.top !== undefined) {
    return {
      left: pageImage.image.left + pagePositionX - (pageImage.pixelCrop ? pageImage.pixelCrop.x : 0),
      top: pageImage.image.top + pagePositionY - (pageImage.pixelCrop ? pageImage.pixelCrop.y : 0),
    };
  } else {
    return null;
  }
};

export const arePageImageDataEqual = (item1: pageImageData, item2: pageImageData): boolean => {
  return (
    item1.pageNumber === item2.pageNumber &&
    item1.orderIndex === item2.orderIndex &&
    _.isEqual(item1.pixelCrop, item2.pixelCrop)
  );
};

export const getTargetPageImageToInsertAnnotation = (
  pageImages: pageImageData[],
  targetObject: fabric.Object | undefined,
): pageImageData | undefined => {
  return pageImages.length === 1 ? pageImages[0] : pageImages.find((item) => targetObject === item.image);
};

export const getImageUnderPointer = (
  pageImages: pageImageData[],
  pointer: { x: number; y: number },
): pageImageData | null => {
  for (let i = 0; i < pageImages.length; i++) {
    const obj = pageImages[i].image! as fabric.Object;
    if (!obj) return null;
    if (
      pointer.x >= obj.left! &&
      pointer.x <= obj.left! + obj.width! * obj.scaleX! &&
      pointer.y >= obj.top! &&
      pointer.y <= obj.top! + obj.height! * obj.scaleY!
    ) {
      return pageImages[i];
    }
  }
  return null;
};

export const getNormalizedAngle = (angle: number) => {
  return angle >= 0 ? angle : angle + 360;
};

export const getAngle = (originX: number, originY: number, targetX: number, targetY: number) => {
  const dx = originX - targetX;
  const dy = originY - targetY;
  if (Math.abs(dx) < 2 && Math.abs(dy) < 2) {
    return 0;
  }
  const theta = Math.atan2(-dy, -dx);
  const result = (theta * 180) / Math.PI;
  return getNormalizedAngle(result);
};

export const getDistance = (X1: number, Y1: number, X2: number, Y2: number) => {
  return Math.sqrt(Math.pow(X2 - X1, 2) + Math.pow(Y2 - Y1, 2));
};
